import { Text } from '@react-three/drei';
import { type ThreeEvent } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { useNavigationStore } from '@/store/useNavigationStore';
import { palette } from '@/theme/palette';

/**
 * 3D "Geri Dön" button rendered as a child of <XROrigin>. Because it
 * lives in the XR origin's reference frame it follows the user when
 * they snap-teleport into a page, so the button is always 1.5 m in
 * front of them at chest height — clickable with the controller ray.
 *
 * Hidden when there is no immersive XR session (so 2D viewers stay
 * clean) and when the navigation phase is `idle`.
 */
export function VRReturnButton() {
  const session = useXR((s) => s.session);
  const phase = useNavigationStore((s) => s.phase);
  const returnHome = useNavigationStore((s) => s.returnHome);

  if (!session) return null;
  if (phase === 'idle') return null;

  const handleClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    returnHome();
  };

  // Small floating plate at the user's bottom-right field of view —
  // sits at chest-height plus a forward offset so it reads like a HUD
  // button on the page rather than a wall in the user's face.
  return (
    <group position={[0.55, 0.55, -1.4]} onClick={handleClick}>
      <mesh>
        <boxGeometry args={[0.55, 0.18, 0.04]} />
        <meshStandardMaterial
          color="#0d1b4a"
          emissive={palette.cyan}
          emissiveIntensity={0.7}
          metalness={0.4}
          roughness={0.5}
        />
      </mesh>
      <Text
        position={[0, 0, 0.025]}
        fontSize={0.07}
        color={palette.text}
        anchorX="center"
        anchorY="middle"
        letterSpacing={-0.005}
      >
        ← Geri Dön
      </Text>
    </group>
  );
}
